import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { clearSession, getToken, getUsername, isTokenValid, setToken, setUsername, setUserLanguagePref } from '../services/storage';
import { clearCachedUserData } from '../services/session';

// Último usuario que inició sesión (NO se borra en logout). Permite detectar
// un cambio de usuario y limpiar las cachés del anterior en el siguiente login.
const LAST_AUTH_USER_KEY = 'LAST_AUTH_USER';
import { setTokenInDB } from '../services/db';
import { login as loginApi } from '../services/api';
import { setAnalyticsUser, track } from '../services/analytics';
import { setErrorTrackingUser } from '../services/errorTracking';
import { disablePushOnLogout } from '../services/pushNotifications';
import { getClaims } from '../services/claims';
import type { UserClaims } from '../services/claims';
import { resolveLanguageForUser, consumePendingLoginLanguage } from '../i18n';

interface AuthContextType {
  isAuthenticated: boolean;
  // True mientras se está cerrando la sesión (avisa al back, rompe la
  // subscripción push, limpia estado). Lo consume el overlay global de
  // "Cerrando sesión" que bloquea la pantalla durante el proceso.
  isLoggingOut: boolean;
  username: string | null;
  claims: UserClaims;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Países de habla hispana (ISO-2). Un usuario con alguno de estos → español;
// el resto → inglés. Hoy en Aldahra: España y Argentina. Ampliar aquí si se
// suman más países hispanohablantes.
const SPANISH_SPEAKING_COUNTRIES = ['ES', 'AR'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => isTokenValid());
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [username, setUsernameState] = useState<string | null>(() => getUsername());
  // Capacidades derivadas del JWT (países, self-managed, view-scope, admin). Se
  // recalculan al iniciar/cerrar sesión — el token vive en localStorage.
  const claims = useMemo(() => getClaims(), [isAuthenticated, username]);

  // Idioma del usuario al iniciar sesión / rehidratar: su preferencia manual si
  // eligió una (login, header o Ajustes); si no, el idioma por defecto según sus
  // países (con algún país de habla hispana → español; si no → inglés). Como la
  // preferencia es por usuario, tras un logout el siguiente usuario no hereda el
  // idioma del anterior. NO depende del idioma activo, así que un cambio manual
  // desde el header/Ajustes no se revierte en la sesión en curso.
  const spanishSpeaking = claims.userCountries.some((c) => SPANISH_SPEAKING_COUNTRIES.includes(c));
  useEffect(() => {
    if (isAuthenticated) resolveLanguageForUser(username, spanishSpeaking);
  }, [isAuthenticated, spanishSpeaking, username]);

  useEffect(() => {
    // Re-sincronizamos el token con IndexedDB al arrancar. setToken() ya lo
    // hace en el login, pero usuarios que iniciaron sesión antes de añadir
    // el authToken store no lo tienen — sin esto el SW no puede hacer
    // peticiones autorizadas en background.
    const tk = getToken();
    if (tk && isTokenValid()) {
      setTokenInDB(tk).catch(() => {});
    }
    // Si la sesión ya estaba abierta al arrancar la app, reidentificamos
    // al usuario en la telemetría sin hacer login otra vez.
    const u = getUsername();
    if (u && tk && isTokenValid()) {
      setAnalyticsUser(u);
      setErrorTrackingUser(u);
    }
    const interval = setInterval(() => {
      if (getToken() && !isTokenValid()) {
        logout();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const login = async (email: string, password: string) => {
    track({ name: 'login_attempt', params: { method: 'password' } });
    const body = {
      client_id: 'appController.client',
      client_secret: 'secret',
      grant_type: 'password',
      password,
      scope: 'api1 offline_access',
      username: email,
    };
    try {
      const response = await loginApi(body);
      const data: unknown = response.data;
      // La API devuelve el JWT directamente como string plano
      const token: string = typeof data === 'string' && data.startsWith('eyJ')
        ? data
        : (typeof data === 'string'
            ? (JSON.parse(data) as { access_token: string }).access_token
            : (data as { access_token: string }).access_token);
      // Si entra un usuario DISTINTO al último, borramos las cachés del anterior
      // ANTES de dar por autenticado (así la primera carga ya trae datos limpios
      // y nunca se muestran los del usuario previo). Mismo usuario → se conserva
      // la caché para que cargue rápido.
      const lastUser = localStorage.getItem(LAST_AUTH_USER_KEY);
      if (lastUser && lastUser !== email) {
        await clearCachedUserData();
      }
      localStorage.setItem(LAST_AUTH_USER_KEY, email);

      // Si el usuario eligió idioma en el toggle del login, lo persistimos como SU
      // preferencia (a partir de aquí toda la app —avisos, guías de instalación,
      // etc.— va en ese idioma). Sin elección explícita no se toca nada: quien ya
      // usaba español sigue en español.
      const chosenLang = consumePendingLoginLanguage();
      if (chosenLang) setUserLanguagePref(email, chosenLang);

      setToken(token);
      setUsername(email);
      setUsernameState(email);
      setIsAuthenticated(true);
      setAnalyticsUser(email);
      setErrorTrackingUser(email);
      track({ name: 'login_success' });
    } catch (err: unknown) {
      const ax = err as { isAxiosError?: boolean; response?: { status?: number } };
      const reason: 'wrong_credentials' | 'network' | 'server' | 'unknown' =
        !ax?.response ? 'network' :
        ax.response.status === 400 || ax.response.status === 401 ? 'wrong_credentials' :
        (ax.response.status ?? 0) >= 500 ? 'server' : 'unknown';
      track({ name: 'login_failure', params: { reason } });
      throw err;
    }
  };

  const logout = async () => {
    // Overlay "Cerrando sesión" en pantalla mientras hacemos el trabajo
    // best-effort de cierre. Centralizado aquí para que cubra por igual el
    // botón del header y el de Ajustes.
    setIsLoggingOut(true);
    try {
      // Avisar al back y romper la subscripción push ANTES de borrar el JWT
      // (después sería 401). Nunca bloquea: tiene timeout interno y se traga
      // los errores.
      await disablePushOnLogout().catch(() => {});
    } finally {
      clearSession();
      setIsAuthenticated(false);
      setUsernameState(null);
      setAnalyticsUser(null);
      setErrorTrackingUser(null);
      setIsLoggingOut(false);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoggingOut, username, claims, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
